// Minimap + FPS HUD (client). A circular top-down radar of the loaded terrain,
// color-coded by block material, with a facing arrow at the center and an FPS
// readout. Toggle with M. This is an early, simplified take on the full D15/UI-8
// map (crafted map items, zoom levels) that lands with M8.
//
// Perf: the radar is rasterized into a small N×N ImageData and blitted once with
// drawImage (nearest-neighbor upscale) — NOT thousands of fillRect calls, which
// crater the frame rate. Redraw is throttled; the FPS text updates every frame.
import { MINIMAP_RADII } from '../../sim/config';
import { buildStateMapColors, type MapPalette } from '../../sim/game/cartography';
import type { Game } from '../boot';

const SIZE = 160; // on-screen px
const GRID = 64; // sampled cells per side
const REDRAW_MS = 150;

export class MiniMap {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly grid: HTMLCanvasElement;
  private readonly gridCtx: CanvasRenderingContext2D;
  private readonly img: ImageData;
  private readonly fpsEl: HTMLDivElement;
  private readonly rgbByState: MapPalette;
  private visible = true;
  private lastDraw = 0;
  /** Zoom index into MINIMAP_RADII (UI-8, §13); cycled with N. */
  private zoomIdx = 0;

  get radius(): number { return MINIMAP_RADII[this.zoomIdx]!; }
  cycleZoom(): number { this.zoomIdx = (this.zoomIdx + 1) % MINIMAP_RADII.length; this.lastDraw = 0; return this.radius; }

  /** Radar RGB the minimap would paint for the column top at (wx,wz), or null. */
  colorAt(wx: number, wz: number): readonly [number, number, number] | null {
    return this.rgbByState[this.game.world.columnTopState(wx, wz)] ?? null;
  }

  constructor(private readonly game: Game, mount: HTMLElement) {
    // Shared UI-8 palette — identical base colours the crafted map uses (M18).
    this.rgbByState = buildStateMapColors(game.registries);

    const root = document.createElement('div');
    root.style.cssText =
      `position:fixed;top:12px;right:12px;width:${SIZE}px;pointer-events:none;` +
      'font:600 12px ui-monospace,monospace;color:#dfe6f0;text-shadow:0 1px 2px #000;' +
      'transform:scale(var(--bb-scale,1));transform-origin:top right;';

    this.fpsEl = document.createElement('div');
    this.fpsEl.style.cssText = 'text-align:right;margin-bottom:4px;';

    this.canvas = document.createElement('canvas');
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    this.canvas.style.cssText = 'border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,.5);display:block;';
    this.ctx = this.canvas.getContext('2d')!;
    this.ctx.imageSmoothingEnabled = false;

    // Offscreen grid we rasterize into, then blit upscaled.
    this.grid = document.createElement('canvas');
    this.grid.width = GRID;
    this.grid.height = GRID;
    this.gridCtx = this.grid.getContext('2d')!;
    this.img = this.gridCtx.createImageData(GRID, GRID);

    root.appendChild(this.fpsEl);
    root.appendChild(this.canvas);
    mount.appendChild(root);

    document.addEventListener('keydown', (e) => {
      if (e.code === 'KeyM') {
        this.visible = !this.visible;
        root.style.display = this.visible ? 'block' : 'none';
      } else if (e.code === 'KeyN' && this.visible) {
        this.cycleZoom(); // UI-8 zoom radii {32,64,128}
      }
    });
  }

  /** Called each render frame; FPS updates always, the radar self-throttles. */
  update(now = performance.now()): void {
    this.fpsEl.textContent = `${this.game.fps} FPS`;
    if (!this.visible || now - this.lastDraw < REDRAW_MS) return;
    this.lastDraw = now;
    this.draw();
  }

  private draw(): void {
    const { body, yaw } = this.game.player;
    const px = Math.floor(body.x), pz = Math.floor(body.z);
    const step = (this.radius * 2) / GRID;
    const data = this.img.data;

    for (let j = 0; j < GRID; j++) {
      const wz = pz + Math.round((j - GRID / 2) * step);
      for (let i = 0; i < GRID; i++) {
        const wx = px + Math.round((i - GRID / 2) * step);
        const rgb = this.rgbByState[this.game.world.columnTopState(wx, wz)];
        const o = (j * GRID + i) * 4;
        if (rgb) {
          data[o] = rgb[0]; data[o + 1] = rgb[1]; data[o + 2] = rgb[2]; data[o + 3] = 255;
        } else {
          data[o] = 11; data[o + 1] = 14; data[o + 2] = 20; data[o + 3] = 255; // background
        }
      }
    }
    this.gridCtx.putImageData(this.img, 0, 0);

    const ctx = this.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(this.grid, 0, 0, GRID, GRID, 0, 0, SIZE, SIZE);
    ctx.restore();

    // Facing arrow (north-up: +z is down, so yaw 0 points up).
    const cx = SIZE / 2, cy = SIZE / 2;
    const fx = -Math.sin(yaw), fy = -Math.cos(yaw);
    const rx = -fy, ry = fx;
    ctx.fillStyle = '#ff5252';
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx + fx * 8, cy + fy * 8);
    ctx.lineTo(cx - fx * 6 + rx * 5, cy - fy * 6 + ry * 5);
    ctx.lineTo(cx - fx * 6 - rx * 5, cy - fy * 6 - ry * 5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.strokeStyle = 'rgba(0,0,0,.6)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
  }
}
